// ts-rest contract for the overdrawn-cash report (issue #44 / PRD #21).
//
// The overdrawn-cash surface is a snapshot-derived read view sourced from
// the `overdrawn_cash_accounts` file type under `scope='current'`
// (ADR-0006). It carries the same response envelope as every other
// snapshot-derived surface — rows wrapped with the `versionRef` they
// were served from (`@/schemas/version-ref`).
//
// Endpoints under `/api/cash/overdrawn`:
//   GET  /api/cash/overdrawn                  — read active or named snapshot
//   POST /api/cash/overdrawn/uploads          — upload a fresh CSV (multipart)
//
// The two row-level Actions (`AssignOverdraftOwner`, `ResolveOverdraftRow`)
// ship in issue #48 — deliberately out of scope here.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { snapshotResponse, SnapshotQuerySchema } from '../schemas/version-ref';

const c = initContract();

// ---------------------------------------------------------------------------
// Row shape — mirrors the `OdCashRow` Object Type (the snake_case persistence
// shape is what the read endpoint emits; no DTO renaming happens on the wire).
// ---------------------------------------------------------------------------

export const odCashRowSchema = z.object({
    id: z.string(),
    snapshotId: z.string(),
    // Position in the parsed file (0-indexed). Stable per upload — keys the
    // overdraft-resolution side table introduced in #48.
    rowIndex: z.number().int().nonnegative(),
    clientReference: z.string(),
    investmentManager: z.string(),
    accountReference: z.string(),
    cashAccountName: z.string(),
    currency: z.string(),
    postedBalance: z.number(),
    registration: z.string(),
    lastRecalculatedOn: z.string(),
    matchedUserId: z.string().nullable(),
    unresolvedImString: z.string().nullable(),
    // Derived display fields joined from the OverdraftResolution side table
    // on read (#48). The underlying OdCashRow stays immutable; these
    // attachments survive snapshot rollback by keying on
    // (snapshotId, rowIndex).
    ownerUserId: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    // Escalation state joined from the OverdraftResolution side table (feedback
    // Page 2). `escalationLevel > 0` means the row was escalated to Compliance;
    // the reason + timestamp drive the Desk "Escalated" marker and the Comply
    // escalation queue.
    escalationLevel: z.number().int().nonnegative(),
    escalatedAt: z.string().nullable(),
    escalationReason: z.string().nullable(),
});
export type OdCashRowDto = z.infer<typeof odCashRowSchema>;

export const overdrawnCashResponseSchema = snapshotResponse(z.array(odCashRowSchema));
export type OverdrawnCashResponse = z.infer<typeof overdrawnCashResponseSchema>;

export const uploadOverdrawnCashResponseSchema = z.object({
    ok: z.literal(true),
    scope: z.literal('current'),
    snapshotId: z.string(),
    versionN: z.number().int(),
    rowCount: z.number().int(),
    // Per-outcome counts for the alias-resolution step run at parse time.
    // `resolved` is the count of rows whose `investmentManager` resolved to
    // exactly one user; `unresolved` carries every other row (zero matches,
    // multiple matches, or empty IM string).
    resolved: z.number().int(),
    unresolved: z.number().int(),
});
export type UploadOverdrawnCashResponse = z.infer<typeof uploadOverdrawnCashResponseSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

// ---------------------------------------------------------------------------
// Issue #48 — overdrawn-cash row Actions (PRD #21).
//
// Two row-level Actions wired off the `/ops/cash/overdrawn` view. Both are
// keyed by `(snapshotId, rowIndex)` so the operator state persists across
// snapshot rollback (#31) — the underlying `OdCashRow` is immutable.
// ---------------------------------------------------------------------------

export const assignOverdraftOwnerRequestSchema = z.object({
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    ownerUserId: z.string().min(1),
    assignmentReason: z.string().min(1).optional(),
});
export type AssignOverdraftOwnerRequest = z.infer<typeof assignOverdraftOwnerRequestSchema>;

export const resolveOverdraftRowRequestSchema = z.object({
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    resolutionNote: z.string().min(1),
});
export type ResolveOverdraftRowRequest = z.infer<typeof resolveOverdraftRowRequestSchema>;

// Feedback Page 2 — escalate an overdrawn-cash row to Compliance. Reuses the
// (snapshotId, rowIndex) key; the reason is collected from the IM in a dialog
// and recorded onto the OverdraftResolution side table.
export const escalateOverdrawnCashRequestSchema = z.object({
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    escalationReason: z.string().min(1),
});
export type EscalateOverdrawnCashRequest = z.infer<typeof escalateOverdrawnCashRequestSchema>;

export const overdraftActionResponseSchema = z.object({
    ok: z.literal(true),
});
export type OverdraftActionResponse = z.infer<typeof overdraftActionResponseSchema>;

// ---------------------------------------------------------------------------
// Cash Management — full cash-positions surface (sourced from the
// `cash_accounts` CashAccountsExport snapshot). Each position carries its
// derived threshold status and the Open CashAlert (if any) the per-row
// Acknowledge / Escalate actions target.
// ---------------------------------------------------------------------------

export const cashPositionStatusSchema = z.enum([
    'Normal',
    'Low Cash Alert',
    'Excess Cash',
    'Overdrawn',
]);

/** The Open CashAlert attached to a position (null when within normal bounds). */
export const cashPositionAlertSchema = z.object({
    id: z.string(),
    alertType: z.enum(['LowCash', 'Overdrawn', 'ExcessCash']),
    status: z.enum(['Open', 'Resolved']),
    severity: z.string(),
    acknowledgedAt: z.string().nullable(),
    escalationLevel: z.number().int(),
});
export type CashPositionAlertDto = z.infer<typeof cashPositionAlertSchema>;

export const cashPositionRowSchema = z.object({
    // The durable CashPosition id (deterministic from the cash-account ref) —
    // stable across re-uploads.
    id: z.string(),
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    accountRef: z.string(),
    cashAccountName: z.string(),
    clientName: z.string(),
    clientReference: z.string(),
    investmentManager: z.string(),
    currency: z.string(),
    availableBalance: z.number(),
    postedBalance: z.number(),
    clearedBalance: z.number(),
    // Headline balance = posted balance (the surface decision).
    balance: z.number(),
    status: cashPositionStatusSchema,
    alert: cashPositionAlertSchema.nullable(),
});
export type CashPositionRowDto = z.infer<typeof cashPositionRowSchema>;

export const cashPositionsResponseSchema = snapshotResponse(z.array(cashPositionRowSchema));
export type CashPositionsResponse = z.infer<typeof cashPositionsResponseSchema>;

export const uploadCashAccountsResponseSchema = z.object({
    ok: z.literal(true),
    scope: z.literal('current'),
    snapshotId: z.string(),
    versionN: z.number().int(),
    rowCount: z.number().int(),
    // Post-commit downstream compute outcome.
    positionsMaterialised: z.number().int(),
    alertsRaised: z.number().int(),
    alertsResolved: z.number().int(),
});
export type UploadCashAccountsResponse = z.infer<typeof uploadCashAccountsResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const cashContract = c.router(
    {
        /**
         * GET /api/cash/overdrawn
         *
         * Defaults to the active version named by the `(overdrawn_cash_accounts,
         * current)` `SnapshotPointer`. Pass `?version=<ParsedSnapshot.id>` to
         * read a specific historical version. The response wraps rows together
         * with the `versionRef` it resolved.
         *
         * When no upload has ever happened (no pointer), the response returns
         * an empty `rows` array and `versionRef.versionId: null` — "no data
         * yet, honestly", not 404.
         */
        listOverdrawn: {
            method: 'GET',
            path: '/cash/overdrawn',
            query: SnapshotQuerySchema,
            responses: {
                200: overdrawnCashResponseSchema,
                404: errorBody,
            },
            summary: 'List active-or-named-snapshot overdrawn-cash rows (ADR-0006).',
        },
        /**
         * GET /api/desk/cash/overdrawn
         *
         * The Desk (IM) projection of the same `(overdrawn_cash_accounts,
         * current)` snapshot as `listOverdrawn`, but scoped server-side to the
         * signed-in IM's own book: an IM on the `desk` persona sees only rows
         * whose resolved owner/IM is them (`imScopeUserId` — the Desk scoping
         * the rest of the Desk surfaces use). Read-only; the assign/resolve
         * Actions stay on the Ops surface. Same response envelope + empty-state
         * semantics as `listOverdrawn`.
         */
        listOverdrawnForDesk: {
            method: 'GET',
            path: '/desk/cash/overdrawn',
            query: SnapshotQuerySchema,
            responses: {
                200: overdrawnCashResponseSchema,
                404: errorBody,
            },
            summary: "List the signed-in IM's own overdrawn-cash rows (Desk, IM-scoped).",
        },
        /**
         * POST /api/desk/cash/overdrawn/resolve — feedback Page 2.
         * Desk (IM) projection of `resolveOverdraftRow`. Dispatches
         * `ResolveOverdraftRow` under the `desk` persona so an IM can resolve a
         * row in their own book. Same OverdraftResolution side-table effect.
         */
        resolveOverdraftRowForDesk: {
            method: 'POST',
            path: '/desk/cash/overdrawn/resolve',
            body: resolveOverdraftRowRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
            },
            summary: "Record a resolution note on the IM's own overdrawn-cash row (Desk).",
        },
        /**
         * POST /api/desk/cash/overdrawn/escalate — feedback Page 2.
         * Dispatches `EscalateOverdrawnCash` under the `desk` persona, recording
         * the escalation reason onto the OverdraftResolution side table so the
         * row surfaces in the Comply escalation queue.
         */
        escalateOverdrawnCashForDesk: {
            method: 'POST',
            path: '/desk/cash/overdrawn/escalate',
            body: escalateOverdrawnCashRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
            },
            summary: "Escalate the IM's own overdrawn-cash row to Compliance (Desk).",
        },
        /**
         * POST /api/cash/overdrawn/assign — issue #48 / PRD #21.
         * Dispatches `AssignOverdraftOwner`. Returns 404 when `ownerUserId`
         * does not exist in the global users directory.
         */
        assignOverdraftOwner: {
            method: 'POST',
            path: '/cash/overdrawn/assign',
            body: assignOverdraftOwnerRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Assign an owner User to an overdrawn-cash row (registry-dispatched).',
        },
        /**
         * POST /api/cash/overdrawn/resolve — issue #48 / PRD #21.
         * Dispatches `ResolveOverdraftRow`. Writes to the OverdraftResolution
         * side table; the underlying OdCashRow stays immutable.
         */
        resolveOverdraftRow: {
            method: 'POST',
            path: '/cash/overdrawn/resolve',
            body: resolveOverdraftRowRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
            },
            summary: 'Record a resolution note on an overdrawn-cash row (registry-dispatched).',
        },
        /**
         * GET /api/cash/positions
         *
         * Active-or-named-snapshot cash positions for Cash Management, sourced
         * from the `(cash_accounts, current)` snapshot. Each row carries its
         * derived threshold `status` and the Open `CashAlert` (or null) that the
         * Acknowledge / Escalate actions target. Empty `rows` + `versionId: null`
         * when nothing has been uploaded yet (not a 404).
         */
        listCashPositions: {
            method: 'GET',
            path: '/cash/positions',
            query: SnapshotQuerySchema,
            responses: {
                200: cashPositionsResponseSchema,
                404: errorBody,
            },
            summary:
                'List active-or-named-snapshot cash positions with their open alerts (ADR-0006).',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
